import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ActionPerformed, PushNotificationSchema, PushNotifications, Token } from '@capacitor/push-notifications';
import { Platform } from '@ionic/angular';
import { ProfileService } from './profile.service';
import { LocalNotifications, ScheduleOptions } from '@capacitor/local-notifications';
import { LoginService } from './login.service';
import { Device } from '@capacitor/device';
import { environment } from 'src/environments/environment';
import { NotificationService } from './notification.service';
import { MyNotificationTopic } from 'src/models/notification';
import { FCM } from '@capacitor-community/fcm';
import { ParishConfigService } from './parishconfig.service';
import { AdministrativeService } from './administrative.service';

@Injectable({
  providedIn: 'root'
})
export class FcmService {
  myNotificationSubscription: MyNotificationTopic[];

  constructor(private profileService: ProfileService, private router: Router, private platform: Platform, private userService: LoginService,
    private authService: AdministrativeService, private notificationService: NotificationService, private loginService: LoginService,
    private parishService: ParishConfigService) { }

  initPush() {
    if (this.platform.is("ios") || this.platform.is("iphone")) {
      FCM.deleteInstance().then();
    }

    this.registerPush();
  }

  async unregisterPush() {
    if (this.platform.is("ios") || this.platform.is("iphone")) {
      FCM.deleteInstance().then();
    }

    await this.unsubscribeFromTopic(this.loginService.currentUser?.ProfileId);
    await this.unsubscribeFromTopic('Admin');

    if (this.myNotificationSubscription) {
      for (const t of this.myNotificationSubscription) {
        await this.unsubscribeFromTopic(t.TopicName);
      }
    }

    PushNotifications.unregister();
  }

  private async registerPush() {
    PushNotifications.requestPermissions().then((permission) => {
      if (permission?.receive === 'granted') {
        PushNotifications.register();
      } else {
        // No permission for push granted
      }
    });

    this.initializeSubscriptions();

    await PushNotifications.addListener('registration', (t: Token) => {
      Device.getInfo().then(device => {
        if (this.loginService.currentUser?.ProfileId > 0) {

          this.loginService.saveUserDevice(this.loginService.currentUser?.ProfileId, device.manufacturer, device.model,
            device.osVersion, t.value, environment.applicationVersion).subscribe();
        }
      });
    }
    );

    PushNotifications.addListener('registrationError', (error: any) => {
      console.log('Error: ' + JSON.stringify(error));
    });

    PushNotifications.addListener('pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        let not: ScheduleOptions = {
          notifications: [{
            id: Date.now(),
            body: notification.body,
            title: notification.title,
            ongoing: false,
          }]
        };
        const result = await LocalNotifications.schedule(not)
      }
    );

    PushNotifications.addListener('pushNotificationActionPerformed',
      async (notification: ActionPerformed) => {
        let data = notification.notification.data;

        this.userService.getCurrentUser();

        if (this.userService.currentUser?.UserId > 0) {
          this.authService.getNewToken().subscribe(() => {
            if (data.type.toLowerCase() === "birthday" || data.type.toLowerCase() == "visitation") {
              let profileId = data.profileid;
              this.profileService.showProfile(profileId, null, false);
            }
            else if (data.componentpage && data.componentpage.length > 0) {
              this.router.navigateByUrl(data.componentpage);
            }
          });
        }
        else {
          this.router.navigateByUrl('/signin');
        }
      });
  }

  initializeSubscriptions() {
    FCM.getToken()
      .then((response) => {
        let profileId = this.loginService.currentUser?.ProfileId;
        let parishId = this.parishService.getCurrentParishConfig()?.ParishId;

        if (!profileId || profileId < 1) {
          return;
        }

        this.myNotificationSubscription = [];

        this.notificationService.getSubscribedTopicsByProfileId(profileId, response.token).subscribe(result => {
          if (result && result.length > 0) {
            this.myNotificationSubscription = result;

            result.forEach(topic => {
              if (topic.IsCurrent === true) {
                this.subscribeToTopic(topic.TopicName);
              }
              else {
                this.unsubscribeFromTopic(topic.TopicName);
              }
            });

            this.subscribeToTopic(`${parishId}_all`);
            this.subscribeToTopic(`${parishId}_${profileId}`);

            if (this.loginService.currentUser?.IsAdministrator && this.loginService.currentUser?.IsAdministrator === true) {
              this.subscribeToTopic(`${parishId}_Admin`);
            }
          }
        });
      })
      .catch((err) => console.log(err));
  }

  unsubscribeOnLogout(profileId: number, isAdmin: boolean) {
    FCM.getToken()
      .then((response) => {
        let parishId = this.parishService.getCurrentParishConfig()?.ParishId;

        this.notificationService.getSubscribedTopicsByProfileId(profileId, response.token).subscribe(result => {
          if (result && result.length > 0) {
            result.forEach(topic => {
              this.unsubscribeFromTopic(topic.TopicName);
            });

            this.unsubscribeFromTopic(`${parishId}_${profileId}`);

            if (isAdmin === true) {
              this.unsubscribeFromTopic(`${parishId}_Admin`);
            }
          }
        });
      })
      .catch((err) => console.log(err));
  }


  subscribeToTopic(topic) {
    FCM.subscribeTo({ topic: topic })
      .then((r) => console.log(`subscribed to topic ${topic}`))
      .catch((err) => console.log(err));
  }

  unsubscribeFromTopic(topic) {
    FCM.unsubscribeFrom({ topic: topic })
      .then((r) => console.log(`unsubscribed from topic ${topic}`))
      .catch((err) => console.log(err));
  }

  clearNotificationCount() {
    PushNotifications.removeAllDeliveredNotifications();
  }

}